import {
  emptyAnunciosFilters,
  type AnunciosFilters,
} from "@/components/admin/AnunciosFiltersForm";

export const WILDCARD = "todas";

export type Lookup = { id: string; slug?: string | null; nombre?: string };

export type PortalLookups = {
  operaciones: Lookup[];
  usos: Lookup[];
  provincias: Lookup[];
  /** Slug único solo dentro de su provincia: se combina con `provs`. */
  comarcas?: Lookup[];
  ciudades: Lookup[];
  barrios: Lookup[];
};

const splitCsv = (s: unknown): string[] =>
  typeof s === "string" && s.length ? s.split(",").filter(Boolean) : [];

const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

function idsToSlugs(ids: string[], lookup: Lookup[]): string[] {
  const m = new Map(lookup.map((x) => [x.id, x.slug ?? ""] as const));
  // Varias filas pueden compartir slug (p. ej. dos "Leganés" colgando de
  // provincias distintas): deduplicamos para no emitir `leganes,leganes`.
  return Array.from(
    new Set(ids.map((id) => m.get(id) || "").filter(Boolean)),
  );
}
// Un slug puede corresponder a VARIAS filas (ciudades/barrios duplicados en
// BD). Devolvemos todos los ids: el resto de facetas (provincia, ciudad) se
// combinan con AND en la consulta, así que el resultado sigue siendo exacto.
function slugsToIds(slugs: string[], lookup: Lookup[]): string[] {
  const m = new Map<string, string[]>();
  for (const x of lookup) {
    if (!x.slug) continue;
    const arr = m.get(x.slug);
    if (arr) arr.push(x.id);
    else m.set(x.slug, [x.id]);
  }
  const out = new Set<string>();
  for (const s of slugs) for (const id of m.get(s) ?? []) out.add(id);
  return Array.from(out);
}

export function encodePortalUrl(
  f: AnunciosFilters,
  lk: PortalLookups,
  basePath: string = "/portal",
): { pathname: string; search: Record<string, string> } {
  // Va al path cuando todos los ids seleccionados comparten un único slug
  // (incluye el caso de filas duplicadas con el mismo slug).
  const pick = (ids: string[], lookup: Lookup[]) => {
    if (!ids.length) return WILDCARD;
    const slugs = idsToSlugs(ids, lookup);
    return slugs.length === 1 ? slugs[0] : WILDCARD;
  };

  const segs = [
    pick(f.operacion_ids, lk.operaciones),
    pick(f.uso_local_ids, lk.usos),
    pick(f.provincia_ids, lk.provincias),
    pick(f.ciudad_ids, lk.ciudades),
    pick(f.barrio_ids, lk.barrios),
  ];
  while (segs.length && segs[segs.length - 1] === WILDCARD) segs.pop();
  const pathname = segs.length ? `${basePath}/${segs.join("/")}` : basePath;

  const search: Record<string, string> = {};
  const opsSlugs = idsToSlugs(f.operacion_ids, lk.operaciones);
  if (opsSlugs.length > 1) search.ops = opsSlugs.join(",");
  const usosSlugs = idsToSlugs(f.uso_local_ids, lk.usos);
  if (usosSlugs.length > 1) search.usos = usosSlugs.join(",");
  const provsSlugs = idsToSlugs(f.provincia_ids, lk.provincias);
  if (provsSlugs.length > 1) search.provs = provsSlugs.join(",");
  const comarcasSlugs = idsToSlugs(f.comarca_ids, lk.comarcas ?? []);
  if (comarcasSlugs.length) search.comarcas = comarcasSlugs.join(",");
  const ciudadesSlugs = idsToSlugs(f.ciudad_ids, lk.ciudades);
  if (ciudadesSlugs.length > 1) search.ciudades = ciudadesSlugs.join(",");
  const barriosSlugs = idsToSlugs(f.barrio_ids, lk.barrios);
  if (barriosSlugs.length > 1) search.barrios = barriosSlugs.join(",");

  if (f.tipo_local_ids.length) search.tipos = f.tipo_local_ids.join(",");
  if (f.distrito_ids.length) search.distritos = f.distrito_ids.join(",");
  if (f.licencia_ids.length) search.licencias = f.licencia_ids.join(",");
  if (f.estados.length) search.estados = f.estados.join(",");
  if (f.visibilidades.length) search.vis = f.visibilidades.join(",");
  if (f.origenes.length) search.origenes = f.origenes.join(",");
  if (f.estado_inmueble.length) search.est_inm = f.estado_inmueble.join(",");
  if (f.contacto_ids.length) search.contactos = f.contacto_ids.join(",");
  if (f.agencia_ids.length) search.agencias = f.agencia_ids.join(",");
  if (f.cp.trim()) search.cp = f.cp.trim();
  if (f.superficie_min != null) search.smin = String(f.superficie_min);
  if (f.superficie_max != null) search.smax = String(f.superficie_max);
  if (f.aseos_min != null) search.amin = String(f.aseos_min);
  if (f.parking_plazas_min != null) search.pmin = String(f.parking_plazas_min);
  if (f.rating_min != null) search.rating = String(f.rating_min);
  if (f.precio_alquiler_min != null)
    search.almin = String(f.precio_alquiler_min);
  if (f.precio_alquiler_max != null)
    search.almax = String(f.precio_alquiler_max);
  if (f.precio_traspaso_min != null)
    search.trmin = String(f.precio_traspaso_min);
  if (f.precio_traspaso_max != null)
    search.trmax = String(f.precio_traspaso_max);
  if (f.precio_venta_min != null) search.vemin = String(f.precio_venta_min);
  if (f.precio_venta_max != null) search.vemax = String(f.precio_venta_max);
  if (f.rent_bruta_min != null) search.rbmin = String(f.rent_bruta_min);
  if (f.rent_bruta_max != null) search.rbmax = String(f.rent_bruta_max);

  return { pathname, search };
}

export function decodePortalUrl(
  segments: string[],
  search: Record<string, unknown>,
  lk: PortalLookups,
): AnunciosFilters {
  const f: AnunciosFilters = { ...emptyAnunciosFilters };

  const [opSeg, usoSeg, provSeg, ciudadSeg, barrioSeg] = segments;
  if (opSeg && opSeg !== WILDCARD)
    f.operacion_ids = slugsToIds([opSeg], lk.operaciones);
  if (usoSeg && usoSeg !== WILDCARD)
    f.uso_local_ids = slugsToIds([usoSeg], lk.usos);
  if (provSeg && provSeg !== WILDCARD)
    f.provincia_ids = slugsToIds([provSeg], lk.provincias);
  if (ciudadSeg && ciudadSeg !== WILDCARD)
    f.ciudad_ids = slugsToIds([ciudadSeg], lk.ciudades);
  if (barrioSeg && barrioSeg !== WILDCARD)
    f.barrio_ids = slugsToIds([barrioSeg], lk.barrios);

  const ops = splitCsv(search.ops);
  if (ops.length) f.operacion_ids = slugsToIds(ops, lk.operaciones);
  const usos = splitCsv(search.usos);
  if (usos.length) f.uso_local_ids = slugsToIds(usos, lk.usos);
  const provs = splitCsv(search.provs);
  if (provs.length) f.provincia_ids = slugsToIds(provs, lk.provincias);
  const comarcas = splitCsv(search.comarcas);
  if (comarcas.length) f.comarca_ids = slugsToIds(comarcas, lk.comarcas ?? []);
  const ciudades = splitCsv(search.ciudades);
  if (ciudades.length) f.ciudad_ids = slugsToIds(ciudades, lk.ciudades);
  const barrios = splitCsv(search.barrios);
  if (barrios.length) f.barrio_ids = slugsToIds(barrios, lk.barrios);

  f.tipo_local_ids = splitCsv(search.tipos);
  f.distrito_ids = splitCsv(search.distritos);
  f.licencia_ids = splitCsv(search.licencias);
  f.estados = splitCsv(search.estados);
  f.visibilidades = splitCsv(search.vis);
  f.origenes = splitCsv(search.origenes);
  f.estado_inmueble = splitCsv(search.est_inm);
  f.contacto_ids = splitCsv(search.contactos);
  f.agencia_ids = splitCsv(search.agencias);
  f.cp = typeof search.cp === "string" ? search.cp : "";
  f.superficie_min = num(search.smin);
  f.superficie_max = num(search.smax);
  f.aseos_min = num(search.amin);
  f.parking_plazas_min = num(search.pmin);
  f.rating_min = num(search.rating);
  f.precio_alquiler_min = num(search.almin);
  f.precio_alquiler_max = num(search.almax);
  f.precio_traspaso_min = num(search.trmin);
  f.precio_traspaso_max = num(search.trmax);
  f.precio_venta_min = num(search.vemin);
  f.precio_venta_max = num(search.vemax);
  f.rent_bruta_min = num(search.rbmin);
  f.rent_bruta_max = num(search.rbmax);

  return f;
}

export function filtersEqual(a: AnunciosFilters, b: AnunciosFilters): boolean {
  const keys = Object.keys(a) as (keyof AnunciosFilters)[];
  for (const k of keys) {
    const va = a[k];
    const vb = b[k];
    if (Array.isArray(va) && Array.isArray(vb)) {
      if (va.length !== vb.length) return false;
      const sa = [...va].sort();
      const sb = [...vb].sort();
      for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
    } else if (va !== vb) {
      return false;
    }
  }
  return true;
}
